import {getClassificationLabel} from '../components/prosemirror-editor/helpers/classificationLevels';
import {
    COMPARTMENT_OPTIONS,
    CONFIDENCE_OPTIONS,
    DEFAULT_HEADER_DATA,
    DISTRIBUTION_OPTIONS,
    IFC_OPTIONS,
    ORIGINATOR_OPTIONS,
    PRODUCTION_CENTER_OPTIONS,
} from '../components/prosemirror-editor/components/documentHeader/headerFieldOptions';

export const HEADER_META_FIELDS = [
    'dtg',
    'originator',
    'classification',
    'distribution',
    'compartment',
    'confidence',
    'ifc',
    'productionCenter',
    'authors',
    'title',
];

const OPTION_FIELD_PLACEHOLDERS = {
    originator: 'Select originator',
    confidence: 'Select confidence',
    ifc: 'Select IFC',
    productionCenter: 'Select production center',
};

const OPTION_LISTS_BY_FIELD = {
    originator: ORIGINATOR_OPTIONS,
    confidence: CONFIDENCE_OPTIONS,
    ifc: IFC_OPTIONS,
    productionCenter: PRODUCTION_CENTER_OPTIONS,
    distribution: DISTRIBUTION_OPTIONS,
    compartment: COMPARTMENT_OPTIONS,
};

export function toPlainYValue(value) {
    if (value && typeof value.toJSON === 'function') {
        return value.toJSON();
    }
    return value;
}

export function normalizeOptionValue(value, field) {
    const plain = toPlainYValue(value);
    const placeholder = OPTION_FIELD_PLACEHOLDERS[field] || '';

    if (plain === undefined || plain === null || plain === '') {
        return {value: '', label: placeholder};
    }

    if (typeof plain === 'string') {
        return resolveOption(field, plain) || {value: plain, label: plain};
    }

    if (typeof plain === 'object') {
        const optionValue = plain.value ?? '';
        const optionLabel = plain.label || optionValue || placeholder;
        if (!optionValue) {
            return {value: '', label: placeholder};
        }
        return resolveOption(field, optionValue) || {value: optionValue, label: optionLabel};
    }

    return {value: '', label: placeholder};
}

export function normalizeMultiSelectValue(value, field = 'distribution') {
    const plain = toPlainYValue(value);
    if (!Array.isArray(plain)) {
        if (typeof plain === 'string' && plain.trim()) {
            return plain.split(',').map((part) => part.trim()).filter(Boolean).map((part) => (
                resolveOption(field, part) || {value: part, label: part}
            ));
        }
        return [];
    }

    return plain
        .map((entry) => {
            const item = toPlainYValue(entry);
            if (typeof item === 'string') {
                return item ? (resolveOption(field, item) || {value: item, label: item}) : null;
            }
            if (item && typeof item === 'object') {
                const itemValue = item.value ?? '';
                if (!itemValue) return null;
                return resolveOption(field, itemValue) || {
                    value: itemValue,
                    label: item.label ?? itemValue,
                };
            }
            return null;
        })
        .filter(Boolean);
}

/** Stable string keys for comparing multi-select header values (objects or legacy strings). */
export function multiSelectValueKeys(value, field = 'distribution') {
    return normalizeMultiSelectValue(value, field)
        .map((item) => item.value)
        .filter(Boolean)
        .sort();
}

/**
 * ProseMirror block attrs may store classification as a string or {value,label}.
 * @param {unknown} raw
 * @returns {string}
 */
export function blockClassificationLevel(raw) {
    const plain = toPlainYValue(raw);
    if (typeof plain === 'object' && plain !== null && plain.value) {
        return plain.value;
    }
    if (typeof plain === 'string' && plain.trim()) {
        return plain.trim();
    }
    return 'U';
}

/**
 * Normalize block-level distribution/compartment attrs to string tokens.
 * Handles legacy strings ("FOUO"), comma-separated strings, arrays, and {value,label} objects.
 * @param {unknown} value
 * @param {'distribution'|'compartment'} [field]
 * @returns {string[]}
 */
export function blockClassificationTokens(value, field = 'distribution') {
    return multiSelectValueKeys(value, field);
}

/**
 * Build the inline classification prefix shown before paragraph/heading content.
 * Example: U//FOUO/NF//COBRA
 * @param {object|null|undefined} attrs
 * @returns {string}
 */
export function formatBlockClassificationPrefix(attrs) {
    if (!attrs) {
        return 'U';
    }

    const level = blockClassificationLevel(attrs.classificationLevel);
    const distribution = blockClassificationTokens(attrs.distribution, 'distribution');
    const compartment = blockClassificationTokens(attrs.compartment, 'compartment');

    let result = level;
    if (distribution.length > 0) {
        result += '//' + distribution.join('/');
    }
    if (compartment.length > 0) {
        result += '//' + compartment.join('/');
    }
    return result;
}

export function normalizeHeaderValue(field, value) {
    if (field === 'distribution' || field === 'compartment' || field === 'authors') {
        if (field === 'compartment') {
            const plain = toPlainYValue(value);
            if (typeof plain === 'string' && plain.trim()) {
                return plain.split(',').map((part) => part.trim()).filter(Boolean).map((part) => (
                    resolveOption('compartment', part) || {value: part, label: part}
                ));
            }
        }
        return normalizeMultiSelectValue(value, field);
    }

    if (field === 'originator' || field === 'classification' || field === 'confidence' || field === 'ifc' || field === 'productionCenter') {
        if (field === 'classification') {
            const plain = toPlainYValue(value);
            if (typeof plain === 'string' && plain) {
                return {value: plain, label: getClassificationLabel(plain)};
            }
        }
        return normalizeOptionValue(value, field);
    }

    return toPlainYValue(value);
}

/**
 * Reads all header fields out of a Y.Map and returns them as a plain object.
 * @param {import('yjs').Map|null} yDocMeta
 */
export function readHeaderFromYDocMap(yDocMeta) {
    if (!yDocMeta) return {};
    const result = {};
    HEADER_META_FIELDS.forEach((field) => {
        const val = yDocMeta.get(field);
        if (val !== undefined) result[field] = normalizeHeaderValue(field, val);
    });
    return result;
}

/**
 * @param {import('yjs').Doc|null} ydoc
 */
export function readHeaderFromYDoc(ydoc) {
    if (!ydoc) return {};
    try {
        return readHeaderFromYDocMap(ydoc.getMap('documentMeta'));
    } catch (_) {
        return {};
    }
}

function resolveOption(field, value) {
    const options = OPTION_LISTS_BY_FIELD[field];
    if (!options || !value) return null;
    const match = options.find((opt) => opt.value === value);
    return match ? {value: match.value, label: match.label} : null;
}

function pickHeaderField(field, flatValue, nestedValue) {
    const fromFlat = flatValue !== undefined ? normalizeHeaderValue(field, flatValue) : undefined;
    const fromNested = nestedValue !== undefined ? normalizeHeaderValue(field, nestedValue) : undefined;
    if (hasMeaningfulHeaderField(field, fromFlat)) return fromFlat;
    if (hasMeaningfulHeaderField(field, fromNested)) return fromNested;
    if (fromFlat !== undefined) return fromFlat;
    if (fromNested !== undefined) return fromNested;
    return normalizeHeaderValue(field, field === 'authors' ? [] : undefined);
}

/**
 * Converts API report meta (nested security/production/assessment or legacy flat) into
 * the same shape used by DocumentHeader / preview.
 * @param {object|null|undefined} apiMeta
 */
export function apiMetaToHeaderData(apiMeta) {
    if (!apiMeta) {
        return {...DEFAULT_HEADER_DATA};
    }

    const security = apiMeta.security || {};
    const production = apiMeta.production || {};
    const assessment = apiMeta.assessment || {};

    return {
        ...DEFAULT_HEADER_DATA,
        dtg: pickHeaderField('dtg', apiMeta.dtg || apiMeta.dtgIso, production.dtg) || '',
        originator: pickHeaderField('originator', apiMeta.originator, production.originator),
        classification: pickHeaderField('classification', apiMeta.classification, security.classification),
        distribution: pickHeaderField('distribution', apiMeta.distribution, security.distribution),
        compartment: pickHeaderField('compartment', apiMeta.compartment, security.compartment),
        confidence: pickHeaderField('confidence', apiMeta.confidence, assessment.confidence),
        ifc: pickHeaderField('ifc', apiMeta.ifc, security.ifc),
        productionCenter: pickHeaderField('productionCenter', apiMeta.productionCenter, production.productionCenter),
        authors: pickHeaderField('authors', apiMeta.authors, apiMeta.authors),
    };
}

/**
 * Merge two header-shaped objects; primary wins when it has a meaningful value, else fallback.
 * @param {object|null|undefined} primary
 * @param {object|null|undefined} fallback
 */
export function mergeHeaderData(primary, fallback) {
    const merged = {
        ...DEFAULT_HEADER_DATA,
        ...(fallback || {}),
        ...(primary || {}),
    };
    HEADER_META_FIELDS.forEach((field) => {
        if (hasMeaningfulHeaderField(field, primary?.[field])) {
            merged[field] = primary[field];
        } else if (hasMeaningfulHeaderField(field, fallback?.[field])) {
            merged[field] = fallback[field];
        }
    });
    return merged;
}

/**
 * @param {import('yjs').Doc|null} ydoc
 */
export function readSerializableHeaderFromYDoc(ydoc) {
    const header = readHeaderFromYDoc(ydoc);
    return JSON.parse(JSON.stringify(header));
}

function scalarForApiField(field, value) {
    const normalized = normalizeHeaderValue(field, value);
    if (field === 'distribution' || field === 'compartment') {
        const items = Array.isArray(normalized) ? normalized : [];
        const joined = items.map((item) => item?.value).filter(Boolean).join(', ');
        return joined || null;
    }
    if (field === 'classification' || field === 'originator' || field === 'confidence'
        || field === 'ifc' || field === 'productionCenter') {
        if (normalized && typeof normalized === 'object' && 'value' in normalized) {
            return normalized.value || null;
        }
        return typeof normalized === 'string' && normalized.trim() ? normalized.trim() : null;
    }
    if (field === 'dtg') {
        return typeof normalized === 'string' && normalized.trim() ? normalized.trim() : null;
    }
    return normalized ?? null;
}

/**
 * Converts editor header patch objects into dash-api nested ReportMeta for PUT /api/reports/{id}.
 * @param {Record<string, unknown>} updates
 */
export function headerUpdatesToApiMeta(updates = {}) {
    if (!updates || typeof updates !== 'object') {
        return {};
    }

    const security = {};
    const production = {};
    const assessment = {};

    if (Object.prototype.hasOwnProperty.call(updates, 'classification')) {
        security.classification = scalarForApiField('classification', updates.classification);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'ifc')) {
        security.ifc = scalarForApiField('ifc', updates.ifc);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'distribution')) {
        security.distribution = scalarForApiField('distribution', updates.distribution);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'compartment')) {
        security.compartment = scalarForApiField('compartment', updates.compartment);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'dtg')) {
        production.dtg = scalarForApiField('dtg', updates.dtg);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'originator')) {
        production.originator = scalarForApiField('originator', updates.originator);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'productionCenter')) {
        production.productionCenter = scalarForApiField('productionCenter', updates.productionCenter);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'confidence')) {
        assessment.confidence = scalarForApiField('confidence', updates.confidence);
    }

    const meta = {};
    if (Object.keys(security).length > 0) {
        meta.security = security;
    }
    if (Object.keys(production).length > 0) {
        meta.production = production;
    }
    if (Object.keys(assessment).length > 0) {
        meta.assessment = assessment;
    }
    return meta;
}

function hasMeaningfulHeaderField(field, value) {
    if (value === undefined || value === null) {
        return false;
    }
    if (field === 'dtg') {
        return typeof value === 'string' && value.trim().length > 0;
    }
    if (field === 'distribution' || field === 'compartment' || field === 'authors') {
        return Array.isArray(value) && value.length > 0;
    }
    if (typeof value === 'object' && value !== null) {
        return Boolean(value.value);
    }
    return String(value).trim().length > 0;
}

/**
 * True when Y.Map documentMeta has no user-facing header fields (only session keys, etc.).
 * @param {import('yjs').Map|null} yDocMeta
 */
export function isDocumentMetaHeaderEmpty(yDocMeta) {
    if (!yDocMeta) {
        return true;
    }
    return !HEADER_META_FIELDS.some((field) => hasMeaningfulHeaderField(field, yDocMeta.get(field)));
}

/**
 * Writes header-shaped data into Y.Map documentMeta (for hydration / migration).
 * @param {import('yjs').Map} yDocMeta
 * @param {object} headerData
 */
export function applyHeaderDataToYDocMap(yDocMeta, headerData = {}) {
    if (!yDocMeta || !headerData) {
        return;
    }
    if (headerData.title !== undefined && headerData.title !== null) {
        yDocMeta.set('title', headerData.title);
    }
    HEADER_META_FIELDS.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(headerData, field)) {
            return;
        }
        yDocMeta.set(field, normalizeHeaderValue(field, headerData[field]));
    });
}
